import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { AssetLogo } from '@/components/tables/tableParts';
import { exchangeShortName } from '../../../config/constants/exchangeShortNames';
import { ExchangeLogo } from './ExchangeLogo';
import { CountryFlag } from '@/shared/components/CountryFlag';

interface SecurityItemRowProps {
  symbol: string;
  name: string;
  /** Resolved sector NAME (e.g. "Technology") — not the raw Morningstar code. */
  sectorLabel?: string;
  /** ISO 3166-1 alpha-2 country code (e.g. "US") — from Security.country. */
  country?: string;
  /** Exchange MIC (e.g. "XNAS") — from Security.mic. */
  mic?: string | null;
  isHighlighted?: boolean;
  onClick: () => void;
}

// Row layout replicates the Figma QPM UniverseSelection security rows: logo +
// plain-text symbol in a fixed column, the company name with a violet outline
// sector-NAME chip, then the listing country flag + exchange + an
// exchange-tinted activity dot. Every field comes from the real Security model
// (ticker, name, sectorCode->label, country ISO-2, mic). The #9810fa accent is
// the Figma source's deliberate one-off (brighter than --primary), kept literal
// so it stays faithful and reads on light and dark.
export function SecurityItemRow({
  symbol,
  name,
  sectorLabel,
  country,
  mic,
  isHighlighted,
  onClick,
}: SecurityItemRowProps) {
  const exchange = mic ? exchangeShortName(mic) : null;

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex h-12 w-full items-center px-3 text-left transition-colors',
        'border-b border-border/50 hover:bg-primary/5',
        isHighlighted && 'bg-primary/10',
      )}
    >
      {/* Fixed logo + plain-text symbol column, then name + sector chip. */}
      <div className="flex min-w-0 flex-1 items-center gap-4">
        <div className="flex w-[120px] shrink-0 items-center gap-3">
          {/* Shared holdings-table logo (parqet → FMP → monogram), sized to the
              Figma row's 24px footprint. */}
          <AssetLogo ticker={symbol} mic={mic} size="xs" />
          <span className="truncate text-[15px] text-foreground">{symbol}</span>
        </div>
        <div className="flex min-w-0 flex-1 items-center gap-3">
          <span className="truncate text-[15px] text-foreground">{name}</span>
          {sectorLabel && (
            <Badge
              variant="outline"
              className="shrink-0 whitespace-nowrap border-purple-200/50 px-2 py-0.5 text-[12px] font-normal text-primary dark:border-primary/40"
            >
              {sectorLabel}
            </Badge>
          )}
        </div>
      </div>

      {/* Listing country flag + exchange code + the exchange's own mark. */}
      <div className="ml-4 flex shrink-0 items-center gap-3">
        {country && <CountryFlag code={country} />}
        {exchange && (
          <span className="text-[15px] text-foreground">{exchange}</span>
        )}
        {mic && <ExchangeLogo mic={mic} />}
      </div>
    </button>
  );
}
